/**
 * PersonTemplate.tsx — phone, e-mail and combined person lookups.
 * Covers: combined_phone, phone, combined_email, deepweb (non-Pluto), name_api.
 *
 * Consumes the EXISTING normaliser `prepareCommonResult()` from
 * store/searcher/service.ts, so names, emails, addresses, images, birthdays and
 * socialNetworks keep whatever cleanup that function already does. The only
 * thing added on top is `groupProviders()` for the coverage matrix.
 */
import * as React from 'react'
import { Box, Typography } from '@mui/material'
import { parsePhoneNumber } from 'react-phone-number-input'
import { SearchResult } from '../../../store/searcher/reducer'
import { prepareCommonResult } from '../vendor/searcherService'
import { cleanScalar, groupProviders } from '../adapters/providers'
import { FieldTable } from '../ui/primitives'
import { hasMapPins, MapCard } from '../ui/MapCard'
import { ProviderCoverage } from '../ui/ProviderCoverage'
import {
  BreachList, Chips, Cols, Flag, LinkPreviewCard, NoData, Photo, RawCard, ResCard, sentence, ShowMoreButton, SourceBadge, Stats, toImageSrc, usePhotoLightbox, useShowMore, ValueList, ValueRow, useTokens,
} from '../ui/primitives'
import { useLinkPreviews } from '../ui/linkPreview'
import type { TemplateProps } from '../registry'
import { initials, radii, sourceColor } from '../tokens'

/**
 * Fixed height every tile card (all but the last) is stretched to, via
 * ResCard's `fill` prop, regardless of how much of its own content it holds
 * — sized to comfortably fit a capped ValueList (5 rows) or the Employment
 * field table without clipping. Short cards (Photos, Profiles) just carry a
 * bit of empty space at the bottom instead of the ragged column heights the
 * old fixed-column layout produced.
 */
const TILE_HEIGHT = 250

/** The `gap` value `Cols` (ui/primitives.tsx) renders its grid with — needed
 * here too so the Locations+Map block's fixed 2-row height (below) lines up
 * exactly with two real tile rows plus the gap between them, instead of
 * leaving a sliver of empty space or overshooting into a third row. */
const COLS_GAP = 14

/**
 * Group identical values so one row can carry several source badges.
 *
 * `prepareCommonResult` emits items shaped `{ app, name | email | address }`,
 * so the provider key lives on `app`. It also lets malformed values through —
 * Getcontact returns `"undefined,  null,  null"` for e-mail — so every value
 * goes through `cleanScalar` and empties are dropped rather than displayed.
 */
function groupByValue(items: any[], getValue: (i: any) => any): ValueRow[] {
  const map = new Map<string, ValueRow>()
  items.forEach((i) => {
    const raw = getValue(i)
    const v = cleanScalar(raw ?? '')
    if (!v || v === '-') return
    const s = String(i?.app ?? i?.source ?? i?.from ?? '').toLowerCase()
    const found = map.get(v)
    if (found) {
      if (s && !found.sources!.includes(s)) found.sources!.push(s)
    } else {
      map.set(v, { value: v, sources: s ? [s] : [] })
    }
  })
  return Array.from(map.values())
}

// Map card (schematic + live Google map, country matching) moved to
// `../ui/MapCard` — shared with NameScanTemplate, which needed the exact
// same panel for PLUTO/webmii/allServicesNames location data.

// (schematic WorldMap, LiveMap, MapCard — all in ../ui/MapCard now)

/**
 * Google Calendar's own timestamp shape: "2021/03/04 19:00:00 (UTC)", always
 * UTC regardless of the calendar's actual `time_zone` (that field only
 * labels which zone the *analyst* should read the wall-clock time in — the
 * value itself is not shifted). Parsed just far enough to sort by; the
 * string itself is what renders, unchanged, so there is no risk of the
 * display disagreeing with the sort.
 */
const parseCalDate = (s?: string): number => {
  if (!s) return 0
  const m = /^(\d{4})\/(\d{2})\/(\d{2})[ T](\d{2}):(\d{2}):(\d{2})/.exec(s)
  return m ? Date.UTC(+m[1], +m[2] - 1, +m[3], +m[4], +m[5], +m[6]) : 0
}

/** "2021/03/04 19:00:00 (UTC)" -> "2021-03-04", for grouping into calendar cells. */
const dateKeyOf = (s?: string): string | undefined => {
  if (!s) return undefined
  const m = /^(\d{4})\/(\d{2})\/(\d{2})/.exec(s)
  return m ? `${m[1]}-${m[2]}-${m[3]}` : undefined
}

/**
 * "2021/03/04 19:00:00 (UTC)" -> "19:00 (UTC)" — the day-details pane's own
 * header already names the date once ("2021-03-04 · 3 events"), so repeating
 * it on every row below just pushed the actual content (title) further from
 * the left edge for no new information. Seconds are dropped too — Google
 * Calendar always sends ":00" here, never anything finer.
 */
const timeOnly = (s?: string): string => {
  if (!s) return '—'
  const m = /(\d{2}):(\d{2}):\d{2}(.*)$/.exec(s)
  return m ? `${m[1]}:${m[2]}${m[3]}` : s
}

/** Luna's `job.dateRange.{start,end}` (full ISO timestamps) -> "2020 – 2023"
 * / "Since 2020" / "Until 2023" — just the years, which is all a résumé-style
 * job list needs; the time-of-day on these is always midnight noise anyway. */
const jobPeriod = (start?: string, end?: string): string | undefined => {
  const year = (s?: string) => {
    if (!s) return undefined
    const d = new Date(s)
    return Number.isNaN(d.getTime()) ? undefined : d.getFullYear()
  }
  const sy = year(start)
  const ey = year(end)
  if (sy && ey) return sy === ey ? String(sy) : `${sy} – ${ey}`
  if (sy) return `Since ${sy}`
  if (ey) return `Until ${ey}`
  return undefined
}

const MONTH_NAMES = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
]
const WEEKDAYS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun']

/** Never render more than this many rows for one selected day — a daily
 * recurring meeting run for years is still one calendar cell, not a wall of
 * near-identical rows. */
const DAY_EVENTS_CAP = 40

type CalEvent = {
  id: string
  summary: string
  start?: string
  dateKey?: string
  location: string
  status?: string
  htmlLink?: string
}

function NavBtn({ disabled, onClick, children }: { disabled?: boolean; onClick: () => void; children: React.ReactNode }) {
  const tk = useTokens()
  return (
    <Box
      component="button"
      type="button"
      disabled={disabled}
      onClick={onClick}
      sx={{
        width: 19, height: 19, borderRadius: '6px', border: `1px solid ${tk.border}`, background: tk.surface2,
        color: tk.text2, cursor: disabled ? 'default' : 'pointer', opacity: disabled ? 0.35 : 1,
        display: 'grid', placeItems: 'center', fontSize: 10.5, fontFamily: 'inherit', flex: 'none',
        '&:hover': disabled ? {} : { borderColor: tk.borderStrong, color: tk.text, background: tk.surface3 },
      }}
    >
      {children}
    </Box>
  )
}

/**
 * Profiles card: chips are deduped network *names*, but each one can stand
 * for dozens of underlying URLs (Luna alone can return 70+ `personal_profiles`
 * source rows for a handful of actual networks). Clicking a chip reveals the
 * links that were grouped into it — accordion-style, one network open at a
 * time — instead of the count just disappearing into the dedupe.
 */
type ProfileLink = { url: string; image?: string; title?: string }

function ProfileNetworks({ groups }: { groups: { name: string; links: ProfileLink[] }[] }) {
  const tk = useTokens()
  const [openIdx, setOpenIdx] = React.useState<number | null>(groups.length > 0 ? 0 : null)
  const openLinks = openIdx != null ? groups[openIdx].links : []
  const linkList = useShowMore(openLinks, 6)

  return (
    <>
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
        {groups.map((g, i) => {
          const isOpen = openIdx === i
          return (
            <Box
              key={g.name}
              component="button"
              type="button"
              onClick={() => setOpenIdx((cur) => (cur === i ? null : i))}
              sx={{
                display: 'inline-flex', alignItems: 'center', gap: '5px', fontSize: 11.5, fontWeight: 600,
                px: '10px', py: '4px', borderRadius: '8px', border: 0, cursor: 'pointer', fontFamily: 'inherit',
                background: isOpen ? '#1559B0' : 'rgba(45,137,244,.12)', color: isOpen ? '#fff' : '#1559B0',
              }}
            >
              {sentence(g.name)}
              {g.links.length > 1 && (
                <Box component="span" sx={{ fontSize: 9.5, opacity: 0.85 }}>{g.links.length}</Box>
              )}
            </Box>
          )
        })}
      </Box>
      {openIdx != null && (
        <Box sx={{ mt: '10px' }}>
          {openLinks.length === 0 ? (
            <Typography sx={{ fontSize: 11, color: tk.text3 }}>No link for this network.</Typography>
          ) : (
            <>
              <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '8px' }}>
                {linkList.visible.map((link, i) => (
                  <LinkPreviewCard
                    key={i}
                    url={link.url}
                    title={link.title || link.url.replace(/^https?:\/\//, '')}
                    image={link.image}
                  />
                ))}
              </Box>
              <ShowMoreButton open={linkList.open} hiddenCount={linkList.hiddenCount} onClick={() => linkList.setOpen((o) => !o)} />
            </>
          )}
        </Box>
      )}
    </>
  )
}

/**
 * Above this length, a live-fetched description doesn't fit a 1/3-width
 * grid cell without the 2-line clamp cutting off most of the sentence.
 */
const LONG_DESCRIPTION_CHARS = 140

/**
 * Websites card: same `LinkPreviewCard` grid as the Profiles reveal above,
 * but flat — `luna.person.urls` isn't grouped by network the way profile
 * links are, so there's no chip-per-network layer here, just a capped grid.
 *
 * `livePreview` fetches a real og:description per link, and those vary
 * wildly in length — a long one clamped into a narrow 3-up cell reads as
 * two ragged half-sentences, and CSS Grid then stretches every *other* card
 * in that row to match its height, so the whole row looks broken. Cards
 * with the longest fetched descriptions are pulled out into their own
 * full-width row (most breathing room, least clipping) sorted longest
 * first; everything else keeps the original 3-up grid, in its original
 * order. Preview fetches are looked up here (via `useLinkPreviews`, same
 * module cache `LinkPreviewCard` itself reads from below) purely to sort by
 * length — the child cards still do their own `livePreview` render.
 */
function WebsiteLinks({ links }: { links: { url: string; title?: string }[] }) {
  const list = useShowMore(links, 6)
  const previews = useLinkPreviews(list.visible.map((l) => l.url))

  const { long, rest } = React.useMemo(() => {
    const withLen = list.visible.map((link, i) => ({ link, i, len: previews[i]?.data?.description?.length ?? 0 }))
    const long = withLen
      .filter((w) => w.len > LONG_DESCRIPTION_CHARS)
      .sort((a, b) => b.len - a.len)
    const longIdx = new Set(long.map((w) => w.i))
    const rest = withLen.filter((w) => !longIdx.has(w.i))
    return { long, rest }
  }, [list.visible, previews])

  return (
    <>
      {long.length > 0 && (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: '8px', mb: rest.length > 0 ? '8px' : 0 }}>
          {long.map(({ link, i }) => (
            <LinkPreviewCard
              key={i}
              url={link.url}
              title={link.title || link.url.replace(/^https?:\/\//, '')}
              livePreview
            />
          ))}
        </Box>
      )}
      {rest.length > 0 && (
        <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '8px' }}>
          {rest.map(({ link, i }) => (
            <LinkPreviewCard
              key={i}
              url={link.url}
              title={link.title || link.url.replace(/^https?:\/\//, '')}
              livePreview
            />
          ))}
        </Box>
      )}
      <ShowMoreButton open={list.open} hiddenCount={list.hiddenCount} onClick={() => list.setOpen((o) => !o)} />
    </>
  )
}

/**
 * Calendar creator/organizer, deduped by email. Each row gets a small round
 * initials avatar (same look as the page-header avatar) and the email
 * rendered as a themed pill via `Chips` — matching how the Profiles panel
 * shows its network chips — instead of plain note text.
 */
function ContactAvatar({ name, email, image }: { name?: string; email: string; image?: string }) {
  const [broken, setBroken] = React.useState(false)
  const showImage = Boolean(image) && !broken
  return (
    <Box
      sx={{
        width: 28, height: 28, borderRadius: '50%', flex: 'none', overflow: 'hidden',
        display: 'grid', placeItems: 'center', fontSize: 10.5, fontWeight: 800, color: '#fff',
        background: showImage ? undefined : sourceColor(name || email),
      }}
    >
      {showImage ? (
        <Box component="img" src={image} alt="" onError={() => setBroken(true)} sx={{ width: '100%', height: '100%', objectFit: 'cover' }} />
      ) : (
        initials(name || email)
      )}
    </Box>
  )
}

function ContactBookList({ contacts }: { contacts: { email: string; name?: string; self?: boolean; image?: string }[] }) {
  const tk = useTokens()
  const list = useShowMore(contacts, 8)
  return (
    <>
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
        {list.visible.map((c, i) => (
          <Box key={c.email + i} sx={{ display: 'flex', gap: '9px', alignItems: 'center' }}>
            <ContactAvatar name={c.name} email={c.email} image={c.image} />
            <Box sx={{ minWidth: 0, flex: 1 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: '6px', flexWrap: 'wrap' }}>
                <Typography sx={{ fontSize: 13, fontWeight: 600, color: tk.text, wordBreak: 'break-word' }}>
                  {c.name || c.email}
                </Typography>
                {c.self && <Flag tone="ok" label="you" />}
              </Box>
              <Box sx={{ mt: '3px' }}>
                <Chips items={[c.email]} tone="b" />
              </Box>
            </Box>
          </Box>
        ))}
      </Box>
      <ShowMoreButton open={list.open} hiddenCount={list.hiddenCount} onClick={() => list.setOpen((o) => !o)} />
    </>
  )
}

/**
 * A month grid, not a flat list — the events card originally just dumped the
 * most recent rows in order, which for a mailbox with years of daily
 * recurring meetings (6,000+ instances in one real payload) told the analyst
 * almost nothing: the top of the list was always today's standing meeting,
 * over and over. A calendar makes the actual PATTERN visible — which days
 * carry activity, how many, and which don't — and clicking a day is what
 * drills into the rows themselves.
 *
 * Bounded to the month containing the event closest to *now* on first
 * render (most mailboxes' interesting activity clusters around the present,
 * not the earliest 2021 row), and Prev/Next can't wander past the actual
 * data range into permanently-empty years.
 */
function WorkingCalendarCard({ events, meta }: { events: CalEvent[]; meta?: { summary?: string; time_zone?: string } }) {
  const tk = useTokens()

  const byDay = React.useMemo(() => {
    const map = new Map<string, CalEvent[]>()
    events.forEach((e) => {
      if (!e.dateKey) return
      if (!map.has(e.dateKey)) map.set(e.dateKey, [])
      map.get(e.dateKey)!.push(e)
    })
    return map
  }, [events])

  // Computed once on mount, not on every render — re-centring the view on
  // "closest to now" every time the analyst clicks a day would fight their
  // own navigation.
  const initial = React.useMemo(() => {
    const now = new Date()
    if (!events.length) return { y: now.getFullYear(), m: now.getMonth(), day: undefined as string | undefined }
    let closest = events[0]
    let bestDiff = Infinity
    const nowMs = now.getTime()
    events.forEach((e) => {
      const diff = Math.abs(parseCalDate(e.start) - nowMs)
      if (diff < bestDiff) { bestDiff = diff; closest = e }
    })
    const d = new Date(parseCalDate(closest.start))
    return { y: d.getUTCFullYear(), m: d.getUTCMonth(), day: closest.dateKey }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const [cursor, setCursor] = React.useState({ y: initial.y, m: initial.m })
  const [selectedDay, setSelectedDay] = React.useState<string | undefined>(initial.day)

  const bounds = React.useMemo(() => {
    if (!events.length) return null
    let min = Infinity
    let max = -Infinity
    events.forEach((e) => {
      const t = parseCalDate(e.start)
      if (t < min) min = t
      if (t > max) max = t
    })
    const minD = new Date(min)
    const maxD = new Date(max)
    return { minY: minD.getUTCFullYear(), minM: minD.getUTCMonth(), maxY: maxD.getUTCFullYear(), maxM: maxD.getUTCMonth() }
  }, [events])

  const atMin = !!bounds && (cursor.y < bounds.minY || (cursor.y === bounds.minY && cursor.m <= bounds.minM))
  const atMax = !!bounds && (cursor.y > bounds.maxY || (cursor.y === bounds.maxY && cursor.m >= bounds.maxM))

  const shift = (delta: number) => setCursor((c) => {
    const d = new Date(Date.UTC(c.y, c.m + delta, 1))
    return { y: d.getUTCFullYear(), m: d.getUTCMonth() }
  })

  const firstOfMonth = new Date(Date.UTC(cursor.y, cursor.m, 1))
  const daysInMonth = new Date(Date.UTC(cursor.y, cursor.m + 1, 0)).getUTCDate()
  const leadBlanks = (firstOfMonth.getUTCDay() + 6) % 7 // Monday-first grid
  const cells: (number | null)[] = [
    ...Array(leadBlanks).fill(null),
    ...Array.from({ length: daysInMonth }, (_, i) => i + 1),
  ]
  while (cells.length % 7 !== 0) cells.push(null)

  const todayKey = new Date().toISOString().slice(0, 10)
  const selectedEvents = selectedDay ? byDay.get(selectedDay) ?? [] : []
  const dayList = useShowMore(selectedEvents.slice(0, DAY_EVENTS_CAP), 6)

  // Not ResCard(fill) — that wraps ALL children in one shared scroll box, so
  // a long day-details list dragged the compact, already-fully-visible month
  // grid into the same scroll region as it (and, since nothing there capped
  // horizontal overflow, occasionally sideways too). Reproduced here by hand:
  // the calendar stays static at its natural height, and only the
  // day-details column scrolls, each with overflowX pinned to `hidden` so
  // neither can ever grow the card sideways.
  return (
    <Box sx={{
      border: `1px solid ${tk.border}`, borderRadius: `${radii.card}px`, p: '14px 15px', minWidth: 0,
      height: '100%', display: 'flex', flexDirection: 'column', overflow: 'hidden', background: tk.surface,
    }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px', mb: '11px', flex: 'none' }}>
        <Typography component="h3" sx={{ fontSize: 11, textTransform: 'uppercase', letterSpacing: '.08em', color: tk.text3, fontWeight: 700 }}>
          Working calendar
        </Typography>
        <Box component="span" sx={{ background: tk.surface3, color: tk.text2, borderRadius: '20px', px: '7px', fontSize: 10, fontWeight: 700 }}>
          {events.length}
        </Box>
      </Box>

      <Box sx={{
        display: 'grid', gridTemplateColumns: 'minmax(0,1.25fr) minmax(0,1fr)', gap: '18px',
        flex: 1, minHeight: 0, overflow: 'hidden',
        '@media (max-width:820px)': { gridTemplateColumns: '1fr' },
      }}>
        {/* Calendar — natural height, never scrolls on its own. */}
        <Box sx={{ minWidth: 0, overflow: 'hidden' }}>
          {(meta?.summary || meta?.time_zone) && (
            <Typography sx={{ fontSize: 9.5, color: tk.text3, mb: '7px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {[meta?.summary, meta?.time_zone].filter(Boolean).join(' · ')}
            </Typography>
          )}

          {/* `space-between` — the day grid below is full column width again,
              so Prev lines up under Monday and Next under Sunday, same as
              the grid's own left/right edges, with the month name centred
              between them. */}
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: '6px' }}>
            <NavBtn disabled={atMin} onClick={() => shift(-1)}>‹</NavBtn>
            <Typography sx={{ fontSize: 10.5, fontWeight: 700, color: tk.text, whiteSpace: 'nowrap' }}>
              {MONTH_NAMES[cursor.m]} {cursor.y}
            </Typography>
            <NavBtn disabled={atMax} onClick={() => shift(1)}>›</NavBtn>
          </Box>

          <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(7,1fr)', mb: '2px' }}>
            {WEEKDAYS.map((w) => (
              <Box key={w} sx={{ textAlign: 'center', fontSize: 7, fontWeight: 700, color: tk.text3, textTransform: 'uppercase' }}>
                {w[0]}
              </Box>
            ))}
          </Box>

          <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(7,1fr)', gap: '4px' }}>
            {cells.map((day, i) => {
              if (day === null) return <Box key={i} />
              const key = `${cursor.y}-${String(cursor.m + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`
              const count = byDay.get(key)?.length ?? 0
              const isToday = key === todayKey
              const isSelected = key === selectedDay
              return (
                <Box
                  key={i}
                  component="button"
                  type="button"
                  disabled={!count}
                  onClick={() => setSelectedDay(key)}
                  title={count ? `${count} event${count === 1 ? '' : 's'}` : undefined}
                  sx={{
                    position: 'relative', height: 20, border: 0, borderRadius: '6px',
                    background: isSelected ? tk.brand050 : 'transparent',
                    outline: isToday ? `1.5px solid ${tk.borderStrong}` : 'none', outlineOffset: '-1.5px',
                    color: tk.text, fontSize: 9.5, fontWeight: count ? 700 : 500, fontFamily: 'inherit',
                    cursor: count ? 'pointer' : 'default', opacity: count ? 1 : 0.4,
                    display: 'grid', placeItems: 'center',
                    '&:hover': count ? { background: isSelected ? tk.brand100 : tk.surface3 } : {},
                  }}
                >
                  {day}
                  {count > 0 && (
                    <Box
                      component="span"
                      sx={{
                        position: 'absolute', top: -3, right: -3, minWidth: 14, height: 14, px: '3px',
                        borderRadius: '999px', background: '#EF4444', color: '#fff', fontSize: 8, fontWeight: 800,
                        display: 'grid', placeItems: 'center', lineHeight: 1, boxShadow: `0 0 0 1.5px ${tk.surface}`,
                      }}
                    >
                      {count > 99 ? '99+' : count}
                    </Box>
                  )}
                </Box>
              )
            })}
          </Box>
        </Box>

        {/* Day details — the only part of this card that scrolls. */}
        <Box sx={{
          minWidth: 0, minHeight: 0, overflowY: 'auto', overflowX: 'hidden', pr: '4px',
          '&::-webkit-scrollbar': { width: '6px' },
          '&::-webkit-scrollbar-track': { background: 'transparent' },
          '&::-webkit-scrollbar-thumb': { background: tk.border, borderRadius: '3px' },
          '&:hover::-webkit-scrollbar-thumb': { background: tk.borderStrong },
        }}>
          <Typography sx={{ fontSize: 10, fontWeight: 700, color: tk.text3, mb: '9px', textTransform: 'uppercase', letterSpacing: '.06em' }}>
            {selectedDay ? `${selectedDay} · ${selectedEvents.length} event${selectedEvents.length === 1 ? '' : 's'}` : 'Select a day'}
          </Typography>
          {!selectedDay && (
            <Typography sx={{ fontSize: 12, color: tk.text3 }}>Click a highlighted day to see what was on the calendar.</Typography>
          )}
          {dayList.visible.map((ev, i) => (
            <Box key={ev.id || i} sx={{ pt: i ? '9px' : 0, mt: i ? '9px' : 0, borderTop: i ? `1px solid ${tk.border}` : 'none' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: '7px', flexWrap: 'wrap' }}>
                <Typography sx={{ fontSize: 10.5, fontWeight: 700, color: tk.text3 }}>{timeOnly(ev.start)}</Typography>
                {ev.status === 'tentative' && <Flag tone="conflict" label="tentative" />}
              </Box>
              {ev.htmlLink ? (
                <Typography
                  component="a"
                  href={ev.htmlLink}
                  target="_blank"
                  rel="noopener noreferrer"
                  sx={{
                    fontSize: 13, fontWeight: 600, color: tk.text, mt: '2px', wordBreak: 'break-word',
                    display: 'block', textDecoration: 'none', cursor: 'pointer',
                    '&:hover': { textDecoration: 'underline' },
                  }}
                >
                  {ev.summary}
                </Typography>
              ) : (
                <Typography sx={{ fontSize: 13, fontWeight: 600, color: tk.text, mt: '2px', wordBreak: 'break-word' }}>{ev.summary}</Typography>
              )}
              {ev.location && <Typography sx={{ fontSize: 10.5, color: tk.text3, mt: '2px', wordBreak: 'break-word' }}>{ev.location}</Typography>}
            </Box>
          ))}
          <ShowMoreButton open={dayList.open} hiddenCount={dayList.hiddenCount} onClick={() => dayList.setOpen((o) => !o)} />
          {selectedEvents.length > DAY_EVENTS_CAP && (
            <Typography sx={{ fontSize: 10, color: tk.text3, mt: '9px' }}>
              Showing the {DAY_EVENTS_CAP} most recent of {selectedEvents.length} events this day.
            </Typography>
          )}
        </Box>
      </Box>
    </Box>
  )
}

export function PersonTemplate({ apiData }: TemplateProps) {
  const tk = useTokens()
  const result = React.useMemo(() => prepareCommonResult(apiData as any), [apiData])
  const providers = React.useMemo(() => groupProviders(apiData), [apiData])

  const names = groupByValue(result.names || [], (n: any) => n.name ?? n.value ?? n)
  const emails = groupByValue(result.emails || [], (e: any) => e.email ?? e.value ?? e)
  const phones = groupByValue(result.phones || [], (p: any) => p.name ?? p.phone ?? p.value ?? p)

  // The number actually SEARCHED (`criteria`) carries its own country, for
  // free, via its dialling prefix — no provider needed, and it can't be
  // contradicted by a stale record the way a provider's own "last known
  // country" can. `parsePhoneNumber` (react-phone-number-input, already a
  // project dependency — same non-throwing call used in store/auth/saga.ts
  // and store/settings/saga.ts) returns `undefined` for anything that
  // isn't a real phone number, so this is a no-op for combined_name/
  // combined_email searches criteria'd by a name or address rather than a
  // number — nothing to gate on `type` for. Pushed as a bare ISO-2 code
  // (`"UA"`, not "Ukraine") to match how other providers already report
  // country-only hits here (TrueCaller's `country` field, etc.) — that's
  // also the exact shape MapCard's bare-alpha-2 matching expects, so this
  // places a country-level pin on the map too, not just a Locations row.
  const criteriaCountry = React.useMemo(() => {
    const criteria = String((apiData as any)?.criteria || '')
    return parsePhoneNumber(criteria)?.country
  }, [apiData])

  const addresses = groupByValue(
    [
      ...(result.addresses || []),
      ...(criteriaCountry ? [{ app: 'criteria', address: criteriaCountry }] : []),
    ],
    (a: any) => a.address ?? a.display ?? a.value ?? a,
  )
  const birthdays = groupByValue(result.birthday || [], (b: any) => b.birthday ?? b.value ?? b)

  // Catch-all for source-specific profile metadata that's neither a name, a
  // contact method, nor a location — currently just Snapchat's bio/
  // subscriber count (see `prepareCommonResult`'s own `entity.from ===
  // 'snapchat'` block), kept out of Contact since neither is a way to reach
  // the person.
  const others: ValueRow[] = (result.others || []).map((o: any) => ({ value: o.value, note: o.note, sources: [o.app] }))

  // Deduped by `display` (Luna's own "Title at Org (since Year)" string) —
  // simple `groupByValue` doesn't fit here since a job is a structured
  // record (title/organization/dates), not a scalar to collapse. Sorted
  // newest-first by start date so the current role leads.
  const jobs = React.useMemo(() => {
    const seen = new Map<string, any>()
    ;(result.jobs || []).forEach((j: any) => {
      const key = j.display || `${j.title || ''}|${j.organization || ''}`
      if (!key.trim() || seen.has(key)) return
      seen.set(key, j)
    })
    return Array.from(seen.values()).sort((a, b) => new Date(b.start || 0).getTime() - new Date(a.start || 0).getTime())
  }, [result.jobs])

  // Same dedup/sort reasoning as `jobs` above.
  const educations = React.useMemo(() => {
    const seen = new Map<string, any>()
    ;(result.educations || []).forEach((e: any) => {
      const key = e.display || `${e.degree || ''}|${e.school || ''}`
      if (!key.trim() || seen.has(key)) return
      seen.set(key, e)
    })
    return Array.from(seen.values()).sort((a, b) => new Date(b.start || 0).getTime() - new Date(a.start || 0).getTime())
  }, [result.educations])

  // Deduped by URL — luna.person.urls is a flat {url, name} list, no dates
  // to sort by, so original order is kept.
  const websites = React.useMemo(() => {
    const seen = new Map<string, any>()
    ;(result.websites || []).forEach((w: any) => {
      if (!w.url || seen.has(w.url)) return
      seen.set(w.url, w)
    })
    return Array.from(seen.values())
  }, [result.websites])

  // mark the first value of each group as primary
  ;[names, emails, phones].forEach((list) => {
    if (list[0]) list[0].flag = { tone: 'primary', label: 'primary' }
  })

  // flag values that appear only once while another source disagrees
  if (names.length > 1) {
    names.slice(1).forEach((n) => {
      if (!n.flag && n.sources && n.sources.length === 1) n.flag = { tone: 'variant', label: 'single source' }
    })
  }

  const rawPhotos = Array.from(new Map((result.images || [])
    .filter((p: any) => p?.image || p?.url)
    .map((p: any) => [String(p.image ?? p.url), p])).values())
  // toImageSrc's own check filters out entries that don't even look like an
  // image (junk values, empty strings) before ever rendering a placeholder
  // tile for them. brokenKeys additionally tracks ones that LOOKED valid
  // but failed to actually load (dead link, corrupt data) — only
  // discoverable once the browser tries, via Photo's onLoadError below —
  // so that tile disappears once that happens instead of sitting there
  // empty for good.
  const [brokenPhotoKeys, setBrokenPhotoKeys] = React.useState<Set<string>>(new Set())
  const photoKey = (p: any) => String(p.image ?? p.url ?? p)
  const photos = rawPhotos.filter((p: any) => toImageSrc(p.image ?? p.url ?? p) && !brokenPhotoKeys.has(photoKey(p)))
  // Snapchat's Bitmoji avatar is meant to sit composited over its own
  // `backgroundUrl` scene, and the snapcode has no equivalent on any other
  // provider — both render as their own tile kind inside the Photos card
  // below (`snapAvatarTiles`/`snapCodeTiles`) rather than the plain square
  // `photos` grid, which would just crop the composition away.
  const snapAvatarTiles = (result.snapchatProfiles || []).filter((p) => p.avatarUrl || p.backgroundUrl)
  const snapCodeTiles = (result.snapchatProfiles || []).filter((p) => p.snapcodeUrl)
  // Lightbox order mirrors the grid's render order (snap avatars, then plain
  // photos, then snapcodes) so `openAt(i)` lines up with each tile's own
  // index. The avatar tile leads with `avatarUrl` (the clearer/closer-cropped
  // image) and falls back to `backgroundUrl` only when no avatar is present —
  // same fallback logic the tile itself uses.
  const { openAt, lightbox } = usePhotoLightbox([
    ...snapAvatarTiles.map((p) => p.avatarUrl || p.backgroundUrl || ''),
    ...photos.map((p: any) => p.image ?? p.url ?? p),
    ...snapCodeTiles.map((p) => p.snapcodeUrl || ''),
  ])
  const socials = result.socialNetworks || []

  // Rich provider records that prepareCommonResult does not model: the LinkedIn
  // person object (emailSearchResult) and the HaveIBeenPwned breach array.
  const entities: any[] = Array.isArray((apiData as any)?.apiDataEntities) ? (apiData as any).apiDataEntities : []
  const linkedin = entities.map((e) => e?.data?.emailSearchResult?.person).find(Boolean)
  // `emailSearchResultHbp` can carry a leading `{ criteria: "..." }` stub —
  // just an echo of the search input, not a breach — alongside the real
  // entries. A real breach always has a name; the stub never does.
  const breaches: any[] = entities
    .flatMap((e) => (Array.isArray(e?.data?.emailSearchResultHbp) ? e.data.emailSearchResultHbp : []))
    .filter((b: any) => Boolean(b?.Title || b?.Name))

  // Glook's Gmail-account scrape (email_info.PROFILE_CONTAINER.calendar) —
  // present only when the mailbox is a Google account and the calendar isn't
  // locked down. Memoized on `apiData` (not `entities`, which is a fresh
  // array every render) since WorkingCalendarCard's own month/day nav state
  // lives in that child component — without this, every click there would
  // re-walk and re-sort however many thousand raw event instances this
  // result carries.
  const calData = React.useMemo(() => {
    const ents: any[] = Array.isArray((apiData as any)?.apiDataEntities) ? (apiData as any).apiDataEntities : []
    const glookPayload = ents.find((e) => e?.data?.emailSearchResultGlook)?.data?.emailSearchResultGlook
    const cal = glookPayload?.email_info?.PROFILE_CONTAINER?.calendar
    const rawItems: any[] = Array.isArray(cal?.events?.items) ? cal.events.items : []
    const calEvents: CalEvent[] = rawItems
      .map((it: any) => {
        const start = it?.start?.date_time || it?.start?.date
        return {
          id: String(it?.id ?? ''),
          summary: cleanScalar(it?.summary ?? '') || '(no title)',
          start,
          dateKey: dateKeyOf(start),
          location: cleanScalar(it?.location ?? ''),
          status: it?.status,
          htmlLink: cleanScalar(it?.html_link ?? '') || undefined,
        }
      })
      .filter((e: CalEvent) => !!e.dateKey)
      .sort((a: CalEvent, b: CalEvent) => parseCalDate(b.start) - parseCalDate(a.start))

    // The searched mailbox's own Google profile photo — the only real photo
    // this payload carries for any of the creator/organizer emails below.
    // There's no way to fetch a photo for anyone else's Gmail address (no
    // such field exists on `creator`/`organizer`, and Google doesn't expose
    // other accounts' avatars without their own auth), so it's only ever
    // attached to the contact whose `self: true` matches the account owner.
    const selfPhoto = cleanScalar(glookPayload?.email_info?.PROFILE_CONTAINER?.profile?.profilePhotos?.PROFILE?.url ?? '') || undefined

    // Every event carries a `creator` and `organizer` — often the same
    // person repeated across hundreds of events. Collapse them into one
    // deduped address book keyed by email (the only field guaranteed to be
    // stable; display_name can vary run to run for the same address).
    const byEmail = new Map<string, { email: string; name?: string; self?: boolean; image?: string }>()
    rawItems.forEach((it: any) => {
      [it?.creator, it?.organizer].forEach((who: any) => {
        const email = cleanScalar(who?.email ?? '')
        if (!email) return
        const name = cleanScalar(who?.display_name ?? '') || undefined
        const self = Boolean(who?.self) || byEmail.get(email)?.self
        const image = self ? selfPhoto : undefined
        const existing = byEmail.get(email)
        if (!existing || (!existing.name && name)) {
          byEmail.set(email, { email, name, self, image: image ?? existing?.image })
        } else if (self) {
          existing.self = true
          existing.image = existing.image ?? selfPhoto
        }
      })
    })
    const contacts = Array.from(byEmail.values()).sort((a, b) => (a.name || a.email).localeCompare(b.name || b.email))

    return { events: calEvents, meta: cal?.details as { summary?: string; time_zone?: string } | undefined, contacts }
  }, [apiData])

  // SMF ("Social Media Finder") probes ~20 networks by username-guessing
  // against the mailbox's local part and reports back one row per network —
  // most with `socialProfileUrl: null` (checked, nothing there). Only the
  // hits are worth a panel; the misses are already accounted for in
  // Provider Coverage's own empty/failed tally, so repeating all 20 here
  // would just be noise duplicating that count.
  const smfLinks = React.useMemo(() => {
    const ents: any[] = Array.isArray((apiData as any)?.apiDataEntities) ? (apiData as any).apiDataEntities : []
    const rows: any[] = ents.find((e) => Array.isArray(e?.data?.emailSearchResultSmf))?.data?.emailSearchResultSmf ?? []
    return rows
      .filter((r: any) => r && typeof r === 'object' && r.social && r.socialProfileUrl)
      .map((r: any) => ({ social: String(r.social), url: String(r.socialProfileUrl) }))
  }, [apiData])

  // `socials` is one row per *occurrence* — the same network shows up once
  // per provider that found it, so a well-covered profile can rack up
  // dozens of rows for a handful of actual networks. Each chip in the
  // Profiles card keeps the (deduped) URLs that were grouped into it, so
  // clicking one can reveal exactly what got folded into that count instead
  // of the detail just disappearing into the dedupe.
  const profileGroups = React.useMemo(() => {
    const byName = new Map<string, Map<string, ProfileLink>>()
    ;(result.socialNetworks || []).forEach((s: any) => {
      const name = String(s.app ?? s.socialNetwork ?? s.name ?? '').trim()
      const url = cleanScalar(s.url ?? '')
      if (!name || !url) return
      if (!byName.has(name)) byName.set(name, new Map())
      const links = byName.get(name)!
      // Prefer whichever occurrence of this URL actually carries a preview
      // (image/title) — Luna can list the same URL more than once, and only
      // some of its passes attach a thumbnail.
      const existing = links.get(url)
      if (!existing || (!existing.image && s.image)) {
        links.set(url, { url, image: cleanScalar(s.image ?? '') || undefined, title: cleanScalar(s.title ?? '') || undefined })
      }
    })
    return Array.from(byName.entries()).map(([name, links]) => ({ name, links: Array.from(links.values()) }))
  }, [result.socialNetworks])

  const hasAnything = names.length || emails.length || phones.length || addresses.length || photos.length || snapAvatarTiles.length || snapCodeTiles.length
  const smfList = useShowMore(smfLinks, 5)

  if (!hasAnything && !providers.withData.length) {
    return <NoData title="No data returned" hint="Results were empty across all sources." />
  }

  const stats = [
    { label: 'Names', value: names.length, sub: names.length ? String(names[0].value) : '—' },
    { label: 'Contact values', value: emails.length + phones.length, sub: `${emails.length} e-mail, ${phones.length} phone` },
    { label: 'Locations', value: addresses.length, sub: addresses.length ? String(addresses[0].value) : '—' },
    { label: 'Profiles', value: socials.length, sub: 'linked accounts' },
    { label: 'Providers', value: `${providers.withData.length}/${providers.declared}`, sub: 'returned data', tone: 'hl' as const },
    ...(result.finalClassification
      ? [{ label: 'Verifier', value: result.finalClassification, sub: `score ${result.verifiedScore ?? '—'}`, tone: 'ok' as const }]
      : []),
  ]

  // `prepareCommonResult` already collects any GPS-tagged place data it finds
  // (currently from Glook's Google-reviews places) into `result.locations` in
  // the exact shape CommonMap wants. Most results won't have any — the
  // Locations card just stays a plain list, same as before.
  const mapPoints = (result.locations || []).filter((p: any) => {
    const lat = typeof p?.lat === 'string' ? parseFloat(p.lat) : p?.lat
    const lng = typeof p?.lng === 'string' ? parseFloat(p.lng) : p?.lng
    return Number.isFinite(lat) && Number.isFinite(lng)
  })

  // Built as a flat, ordered list (not pre-assigned to a column) so Cols can
  // flow them left-to-right and the LAST one — whichever card that ends up
  // being for this particular result — can claim whatever width is left in
  // its row instead of always dropping to a new full-width row.
  //
  // `span` lets one entry claim more than 1 of the 3 columns so it doesn't
  // drift onto its own row depending on how many cards precede it.
  // `composed` marks entries that already build their own multi-card
  // layout, so the render loop skips the automatic single-card `fill`
  // clone for them. `gridColumn`/`gridRow`/`height` are an escape hatch for
  // the one card (Locations+Map) that needs a fixed 2×2 block instead of
  // just a wider single row — see where it's built below.
  const cards: {
    key: string
    node: React.ReactElement
    span?: 1 | 2
    composed?: boolean
    gridColumn?: string
    gridRow?: string
    height?: number
  }[] = []

  if (photos.length > 0 || snapAvatarTiles.length > 0 || snapCodeTiles.length > 0) {
    cards.push({
      key: 'photos',
      node: (
        <ResCard title="Photos" count={photos.length + snapAvatarTiles.length + snapCodeTiles.length}>
          <Box sx={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
            {/* Snapchat Bitmoji leads the grid, same position `bitmojiAvatarUrl`
             * held before (it used to be the first entry pushed into
             * `images`). Background scene full-bleed, avatar layered on top
             * near the bottom. Clickable like any other tile — its image is
             * folded into the lightbox list above (avatarUrl, falling back
             * to backgroundUrl), at the same leading index as here. */}
            {snapAvatarTiles.map((p, i) => (
              <Box
                key={`snap-avatar-${i}`}
                sx={{
                  width: 100, height: 100, borderRadius: `${radii.md}px`, overflow: 'hidden', position: 'relative',
                  background: tk.surface3, border: `1px solid ${tk.border}`,
                  outline: i === 0 ? '2px solid #2D89F4' : 'none', outlineOffset: 1, cursor: 'pointer',
                }}
                onClick={() => openAt(i)}
              >
                {p.backgroundUrl ? (
                  <>
                    <Box sx={{ position: 'absolute', inset: 0 }}>
                      <Photo value={p.backgroundUrl} sx={{ objectFit: 'cover' }} />
                    </Box>
                    {p.avatarUrl && (
                      <Box sx={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'flex-end', justifyContent: 'center', pb: '2px' }}>
                        <Box sx={{ width: '78%', height: '78%' }}>
                          <Photo value={p.avatarUrl} sx={{ objectFit: 'contain' }} />
                        </Box>
                      </Box>
                    )}
                  </>
                ) : (
                  // No background scene to composite onto — just show the
                  // avatar itself, full-bleed like a normal photo tile.
                  p.avatarUrl && <Photo value={p.avatarUrl} sx={{ objectFit: 'cover' }} />
                )}
              </Box>
            ))}

            {photos.map((p: any, i: number) => (
              <Box
                key={i}
                sx={{
                  width: 100, height: 100, borderRadius: `${radii.md}px`, overflow: 'hidden', position: 'relative',
                  background: tk.surface3, border: `1px solid ${tk.border}`,
                  outline: i === 0 && snapAvatarTiles.length === 0 ? '2px solid #2D89F4' : 'none', outlineOffset: 1,
                }}
              >
                <Box sx={{ position: 'absolute', inset: 0, display: 'grid', placeItems: 'center', color: tk.text3 }}>
                  <svg width="36" height="36" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                    <circle cx="12" cy="9" r="3.4" /><path d="M4.5 20c1-3.6 4-5.4 7.5-5.4S18.5 16.4 19.5 20" />
                  </svg>
                </Box>
                <Photo
                  value={p.image ?? p.url ?? p}
                  sx={{ position: 'relative' }}
                  onClick={() => openAt(snapAvatarTiles.length + i)}
                  onLoadError={() => setBrokenPhotoKeys((prev) => new Set(prev).add(photoKey(p)))}
                />
              </Box>
            ))}

            {/* Snapcode (scan-to-add code) — shown on the brand's own
             * yellow so a code exported with a transparent/white
             * background still reads. Trails the lightbox list (after the
             * snap avatar and plain photos), so its index needs both
             * offsets. */}
            {snapCodeTiles.map((p, i) => (
              <Box
                key={`snap-code-${i}`}
                sx={{ width: 100, height: 100, borderRadius: `${radii.md}px`, overflow: 'hidden', background: '#FFFC00', cursor: 'pointer' }}
                onClick={() => openAt(snapAvatarTiles.length + photos.length + i)}
              >
                <Photo value={p.snapcodeUrl} sx={{ objectFit: 'contain' }} />
              </Box>
            ))}
          </Box>
        </ResCard>
      ),
    })
  }

  if (names.length > 0) {
    cards.push({
      key: 'names',
      node: <ResCard title="Names" count={names.length}><ValueList rows={names} /></ResCard>,
    })
  }

  if (emails.length > 0 || phones.length > 0) {
    cards.push({
      key: 'contact',
      node: <ResCard title="Contact" count={emails.length + phones.length}><ValueList rows={[...phones, ...emails]} /></ResCard>,
    })
  }

  if (birthdays.length > 0) {
    cards.push({
      key: 'dates',
      node: <ResCard title="Dates" count={birthdays.length}><ValueList rows={birthdays} /></ResCard>,
    })
  }

  if (others.length > 0) {
    cards.push({
      key: 'others',
      node: <ResCard title="Others" count={others.length}><ValueList rows={others} /></ResCard>,
    })
  }

  if (hasMapPins(mapPoints, addresses)) {
    // The map panel itself — not "Locations", just the map — is pinned to
    // the front (`unshift`, not `push`) and given an explicit 2-column ×
    // 2-row grid area: columns 2–3 of rows 1 and 2. Being first in render
    // order matters: CSS grid places explicitly-positioned items as it
    // reaches them in source order, so this has to claim that 2×2 block
    // before any auto-flowed card (Photos, Names, Locations, ...) can land
    // in it. Column 1 of those same two rows is left open for the grid's
    // normal auto-flow to fill with whichever single-column cards come
    // next — "Locations" (the plain address list, pushed further below)
    // included. `MapCard` already renders its own full `ResCard` (title,
    // Full screen/Show live map buttons), so this needs no extra wrapper —
    // `composed: true` just skips the generic single-card `fill` clone.
    cards.unshift({
      key: 'map',
      composed: true,
      gridColumn: '2 / 4',
      gridRow: '1 / 3',
      height: TILE_HEIGHT * 2 + COLS_GAP,
      node: <MapCard addressCount={addresses.length} mapPoints={mapPoints} addresses={addresses} />,
    })
  }

  if (addresses.length > 0) {
    cards.push({
      key: 'locations',
      node: <ResCard title="Locations" count={addresses.length}><ValueList rows={addresses} /></ResCard>,
    })
  }

  if (profileGroups.length > 0) {
    cards.push({
      key: 'profiles',
      node: (
        <ResCard title="Profiles" count={profileGroups.length}>
          <ProfileNetworks groups={profileGroups} />
        </ResCard>
      ),
    })
  }


  if (linkedin) {
    cards.push({
      key: 'employment',
      node: (
        <ResCard title="Employment">
          <Typography sx={{ fontSize: 13.5, fontWeight: 600, color: tk.text, mb: '8px' }}>{linkedin.headline}</Typography>
          <FieldTable
            rows={([
              ['Location', linkedin.location],
              ['Profile', linkedin.publicIdentifier],
              ['Followers', linkedin.followerCount != null ? Number(linkedin.followerCount).toLocaleString() : undefined],
              ['Member since', linkedin.creationDate ? `${linkedin.creationDate.month}/${linkedin.creationDate.year}` : undefined],
              ['Positions', linkedin.positions?.positionsCount],
              ['Education', linkedin.schools?.educationsCount],
              ['Skills', linkedin.skills?.length],
              ['Languages', linkedin.languages?.length],
            ].filter(([, v]) => v !== undefined && v !== null && v !== '') as [string, React.ReactNode][])}
          />
          {linkedin.showVerificationBadge && (
            <Box sx={{ mt: '9px' }}><Chips items={['Verified profile']} tone="g" /></Box>
          )}
        </ResCard>
      ),
    })
  }

  if (jobs.length > 0) {
    cards.push({
      key: 'jobs',
      node: (
        <ResCard title="Jobs" count={jobs.length}>
          <ValueList
            rows={jobs.map((j: any) => ({
              value: j.title || j.display || j.organization,
              note: [j.organization, jobPeriod(j.start, j.end)].filter(Boolean).join(' · ') || undefined,
            }))}
            cap={6}
          />
        </ResCard>
      ),
    })
  }

  if (educations.length > 0) {
    cards.push({
      key: 'educations',
      node: (
        <ResCard title="Education" count={educations.length}>
          <ValueList
            rows={educations.map((e: any) => ({
              value: e.degree || e.display || e.school,
              note: [e.school, jobPeriod(e.start, e.end)].filter(Boolean).join(' · ') || undefined,
            }))}
            cap={6}
          />
        </ResCard>
      ),
    })
  }

  if (websites.length > 0) {
    cards.push({
      key: 'websites',
      node: (
        <ResCard title="Websites" count={websites.length}>
          <WebsiteLinks links={websites} />
        </ResCard>
      ),
    })
  }

  if (calData.events.length > 0) {
    cards.push({
      key: 'calendar',
      // Same reasoning as the SMF card below: pinned to TILE_HEIGHT on the
      // node itself, not the outer grid's "no fixed height once last" rule
      // — this card can end up last too (no breaches/verified after it).
      composed: true,
      node: (
        <Box sx={{ height: TILE_HEIGHT }}>
          <WorkingCalendarCard events={calData.events} meta={calData.meta} />
        </Box>
      ),
    })
  }

  if (calData.contacts.length > 0) {
    cards.push({
      key: 'contactBook',
      node: (
        <ResCard title="Contact Book" count={calData.contacts.length}>
          <ContactBookList contacts={calData.contacts} />
        </ResCard>
      ),
    })
  }

  if (smfLinks.length > 0) {
    cards.push({
      key: 'smf',
      // Pinned to TILE_HEIGHT on the card's own node, not left to the outer
      // grid's height-only-if-not-last rule — this card can legitimately
      // land last (no breaches/verified after it), and without its own fixed
      // height, "show more" had nothing to scroll *inside*: the fill column
      // just grew taller with the extra rows instead.
      composed: true,
      node: (
        <Box sx={{ height: TILE_HEIGHT }}>
          <ResCard title="Social profiles" count={smfLinks.length} fill>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: '7px' }}>
              {smfList.visible.map((s) => (
                <Box
                  key={s.social}
                  component="a"
                  href={s.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  sx={{
                    display: 'flex', alignItems: 'center', gap: '9px', textDecoration: 'none',
                    borderRadius: '9px', p: '5px 6px', ml: '-6px',
                    '&:hover': { background: tk.surface3 },
                  }}
                >
                  <SourceBadge source={s.social} />
                  <Typography sx={{ fontSize: 12.5, fontWeight: 600, color: tk.text, textTransform: 'capitalize', flex: 'none' }}>
                    {s.social}
                  </Typography>
                  <Typography
                    sx={{
                      fontSize: 10.5, color: tk.text3, ml: 'auto', minWidth: 0,
                      overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                    }}
                  >
                    {s.url.replace(/^https?:\/\//, '')}
                  </Typography>
                </Box>
              ))}
            </Box>
            <ShowMoreButton open={smfList.open} hiddenCount={smfList.hiddenCount} onClick={() => smfList.setOpen((o) => !o)} />
          </ResCard>
        </Box>
      ),
    })
  }

  if (breaches.length > 0) {
    cards.push({
      key: 'breaches',
      // Pinned to TILE_HEIGHT on the card's own node (same fix as the
      // calendar/smf cards above) — Breaches can legitimately land last in
      // the array when there are no verified services after it, and without
      // its own fixed height the outer grid's "no height once last" rule
      // would let "show more" grow the panel instead of scrolling inside it.
      composed: true,
      node: (
        <Box sx={{ height: TILE_HEIGHT }}>
          <ResCard title="Breaches" count={breaches.length} tone="accent" fill>
            <BreachList breaches={breaches} cap={5} />
          </ResCard>
        </Box>
      ),
    })
  }

  if ((result.verifiedServices || []).length > 0) {
    cards.push({
      key: 'verified',
      node: <ResCard title="Verified services" count={result.verifiedServices!.length}><Chips items={result.verifiedServices!} tone="g" cap={10} /></ResCard>,
    })
  }

  // Ergonomics case: the pinned Map is 2 rows tall, but when it has only
  // ONE other card to share the grid with, that lone card is a normal
  // 1-row tile sitting in column 1 — next to a panel twice its height, with
  // nothing else to fill the rest of that column. Rather than leave that
  // as dead space, stretch the lone card to match the Map's full height
  // (`gridRow: '1 / 3'`, same 2-row height) so the two panels read as one
  // balanced pair instead of a short card floating beside a tall one.
  // Doesn't apply once there's a third card — that one naturally lands in
  // column 1's second slot on its own, so nothing needs stretching.
  if (cards.length === 2 && cards[0].gridColumn) {
    cards[1].gridRow = '1 / 3'
    cards[1].height = TILE_HEIGHT * 2 + COLS_GAP
  }

  // Simulates the browser's own grid auto-placement (row-flow, non-dense) to
  // find how much width is left in the LAST card's row, so that card can be
  // stretched to fill it (`lastSpan`) instead of leaving a gap.
  //
  // A flat "3 columns everywhere" model is wrong while the pinned Map card
  // is on screen: it reserves columns 2–3 of rows 1–2 (see where it's built
  // above), so those two rows only have 1 free column each for the
  // auto-flowed cards — row 3 onward is the normal full-width 3-column
  // grid. Treating every row as 3-wide (the old code) meant an auto card
  // landing in row 1 or 2 could get told it has all 3 columns free, hand it
  // `gridColumn: span 3`, and the browser — correctly, since only 1 column
  // is actually free there — would refuse to fit it and push it onto a
  // fresh, otherwise-empty row instead, leaving the real gap (column 1 of
  // rows 1–2) empty. `rowCapacity` fixes the model; the loop below is
  // otherwise the same walk it always was.
  const hasPinnedCard = cards.some((c) => !!c.gridColumn)
  const rowCapacity = (rowIdx: number) => (hasPinnedCard && rowIdx < 2 ? 1 : 3)

  const lastIndex = cards.length - 1
  let row = 0
  let col = 0
  for (let i = 0; i < lastIndex; i++) {
    // The Map card itself (`gridColumn` set) is explicitly positioned
    // outside this flow entirely — the browser places it on its own, so it
    // doesn't advance the counter here.
    if (cards[i].gridColumn) continue
    const span = cards[i].span ?? 1
    if (col + span > rowCapacity(row)) { row += 1; col = 0 }
    col += span
    if (col >= rowCapacity(row)) { row += 1; col = 0 }
  }
  const lastSpan = col === 0 ? rowCapacity(row) : rowCapacity(row) - col

  return (
    <>
      <ResCard title="Key facts">
        <Stats items={stats} />
      </ResCard>

      {/*
        Every tile gets the same fixed `TILE_HEIGHT`, including the last one
        — it used to be left unconstrained (relying on grid `align: stretch`
        against whatever shared its row), which worked when it happened to
        land next to another fixed-height tile but left it free to grow off
        its own natural content height whenever it was the only card in a
        fresh row (lastSpan === 3, nothing to stretch against). That's
        exactly when clicking "+N more" inside a capped ValueList/ProfileNetworks
        grew the whole panel instead of scrolling inside it — ResCard's
        `fill` content box only clips/scrolls once its parent actually has a
        bounded height, and this is the only place that bound gets set. A
        fixed height regardless of position removes that "which card ended
        up last" dependency entirely — see the write-up in this file's
        history if the reasoning needs revisiting.
      */}
      <Cols n={3} align="start">
        {cards.map((c, i) => {
          const isLast = i === lastIndex
          // `lastSpan` only fills leftover row width for a plain 1-wide
          // card that happens to land last — a card with its OWN required
          // span (the Locations+Map pair, `span: 2`) must keep that span
          // regardless of position. Overriding it unconditionally (as this
          // used to) squeezed Locations+Map into a single 1/3-width column
          // whenever it ended up last — which is common for a sparse result
          // (e.g. a reverse_phone row with only Names/Contact/Locations
          // populated) — breaking its internal 2-column layout.
          const span = c.span ?? (isLast ? lastSpan : 1)
          const content = c.composed ? c.node : React.cloneElement(c.node, { fill: true })
          // A hardcoded column/row range (the Map's 2×2 block, or the lone
          // card stretched to match it above) only makes sense against the
          // 3-column desktop grid — `Cols` itself drops to 2 columns
          // (<=1080px) and 1 (<=780px), where column line 4 doesn't exist
          // and would otherwise spill this card into an implicit extra
          // track. Fall back to a normal full-width, auto-placed single
          // row there instead. Keyed off `gridRow`, not just `gridColumn`
          // — the stretched lone card only ever sets `gridRow`.
          const mobileOverride = (c.gridColumn || c.gridRow)
            ? { gridColumn: '1 / -1', gridRow: 'auto', height: TILE_HEIGHT }
            : (isLast ? { gridColumn: '1 / -1' } : undefined)
          return (
            <Box
              key={c.key}
              sx={{
                height: c.height ?? TILE_HEIGHT,
                ...(c.gridColumn ? { gridColumn: c.gridColumn } : (span > 1 ? { gridColumn: `span ${span}` } : {})),
                ...(c.gridRow ? { gridRow: c.gridRow } : {}),
                ...(mobileOverride ? { '@media (max-width:1080px)': mobileOverride } : {}),
              }}
            >
              {content}
            </Box>
          )
        })}
      </Cols>

      <ProviderCoverage summary={providers} tone={providers.withData.length <= 1 ? 'accent' : undefined} />

      {providers.duplicatesDropped > 0 && (
        <Typography sx={{ fontSize: 11.5, color: tk.text3 }}>
          {(apiData as any)?.apiDataEntities?.length} raw entities were returned for {providers.declared} providers.
          Empty and failed repeats are merged above.
        </Typography>
      )}

      <RawCard data={apiData} />
      {lightbox}
    </>
  )
}
